import { useState, useRef, useEffect } from 'react';
import moment from 'moment';
import { Window } from '@progress/kendo-react-dialogs';
import { Button, ButtonGroup } from '@progress/kendo-react-buttons';
import { DatePicker } from '@progress/kendo-react-dateinputs';
import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from '../../../../../messages/en-US';
import { EventFilter } from '../../../../Events/Utilities/EventsApiService';
import GatewayTimelineChart from './GatewayTimelineChart';
import AnomalyDetectorContent from './Dialogs/AnomalyDetector/AnomalyDetectorContent';
import useIdentityStore from '../../../../../Zustand/identityStore';

/** Height of the anomaly toolbar rendered above the chart in anomaly mode. */
const ANOMALY_BUTTONS_HEIGHT = 50;

interface Props {
  serialNumber: string;
  gatewayId: string;
  /** Owning asset, required to create anomalies against the gateway. */
  assetId?: string;
  /** Parent asset display name, prefixed in the window title. */
  assetName?: string;
  onClose: () => void;
}

export default function GatewayTimelineDialog({ serialNumber, gatewayId, assetId, assetName, onClose }: Props) {
  const localizationService = useLocalization();
  const currentUserRole = useIdentityStore((x) => x.user?.profile?.role);
  const [showAnomalyPanel, setShowAnomalyPanel] = useState(false);
  const [hasChartData, setHasChartData] = useState(true);
  const windowRef = useRef<any>(null);
  const [windowHeight, setWindowHeight] = useState<number | null>(null);

  const [eventFilter, setEventFilter] = useState<EventFilter>({
    startDate: moment().subtract(7, 'days').startOf('day').toDate(),
    endDate: moment().endOf('day').toDate(),
  });

  useEffect(() => {
    const windowElement = windowRef.current?.element;
    if (!windowElement) return;
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setWindowHeight(entry.contentRect.height);
      }
    });
    resizeObserver.observe(windowElement);
    setWindowHeight(windowElement.offsetHeight);
    return () => resizeObserver.disconnect();
  }, []);

  const handleStartDateChange = (e: any) => {
    if (e.value) {
      setEventFilter(prev => ({
        ...prev,
        startDate: moment(e.value).startOf('day').toDate(),
      }));
    }
  };

  const handleEndDateChange = (e: any) => {
    if (e.value) {
      setEventFilter(prev => ({
        ...prev,
        endDate: moment(e.value).endOf('day').toDate(),
      }));
    }
  };

  const chartHeight = windowHeight ? windowHeight - 200 : 450;
  const isInAnomalyMode = showAnomalyPanel && currentUserRole === 'SuperAdmin';

  const renderGatewayChart = (height: number) => (
    <GatewayTimelineChart
      serialNumber={serialNumber}
      gatewayId={gatewayId}
      eventFilter={eventFilter}
      setEventFilter={setEventFilter}
      chartParentHeight={height}
      onHasData={setHasChartData}
    />
  );

  const gatewayTitle =`${localizationService.toLanguageString('custom.gateway', enMessages.custom.gateway)} - ${serialNumber}`;

  return (
    <Window
      ref={windowRef}
      title={assetName ? `${assetName} - ${gatewayTitle}` : gatewayTitle}
      onClose={onClose}
      style={{ top: "5vh", left: "5vw", width: "90vw", height: "90vh" }}
      modal={true}
      className="custom-fullscreen-window"
    >
      <div style={{ height: "80vh", display: "flex", flexDirection: "column" }}>
        <div className="flex justify-between mb-4">
          <ButtonGroup>
            <Button
              fillMode="solid"
              style={{
                borderRadius: "0px",
                backgroundColor: "transparent",
                borderColor: "transparent",
                borderBottom: "2px solid var(--kendo-color-primary)",
                color: "var(--kendo-color-primary)"
              }}
            >
              {localizationService.toLanguageString('scheduler.timelineViewTitle', enMessages.scheduler.timelineViewTitle)}
            </Button>
          </ButtonGroup>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {currentUserRole === 'SuperAdmin' && (
              <Button
                togglable
                selected={showAnomalyPanel}
                onClick={() => setShowAnomalyPanel(!showAnomalyPanel)}
                title={localizationService.toLanguageString('custom.anomaly', enMessages.custom.anomaly)}
              >
                {localizationService.toLanguageString('custom.anomaly', enMessages.custom.anomaly)}
              </Button>
            )}
            <DatePicker
              value={eventFilter.startDate}
              onChange={handleStartDateChange}
              max={eventFilter.endDate || undefined}
              width="150px"
            />
            <span className="text-gray-400">-</span>
            <DatePicker
              value={eventFilter.endDate}
              onChange={handleEndDateChange}
              min={eventFilter.startDate || undefined}
              max={moment().toDate()}
              width="150px"
            />
          </div>
        </div>
        <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
          {isInAnomalyMode ? (
            <AnomalyDetectorContent
              assetId={assetId}
              deviceId={gatewayId}
              eventFilter={{ startDate: eventFilter.startDate!, endDate: eventFilter.endDate! }}
              onClose={() => setShowAnomalyPanel(false)}
              containerHeight={chartHeight}
              disableAnomalyButtons={!hasChartData}
            >
              {({ dialogHeight }) => renderGatewayChart(dialogHeight - ANOMALY_BUTTONS_HEIGHT)}
            </AnomalyDetectorContent>
          ) : (
            renderGatewayChart(chartHeight)
          )}
        </div>
      </div>
    </Window>
  );
}
